import React, { useRef, useState } from 'react';

import axios, { CancelTokenSource } from 'axios';
import imageCompression from 'browser-image-compression';
import router from 'next/router';
import { useDropzone } from 'react-dropzone';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import editFile from '@/api/editFile';
import errorMessages from '@/utils/errorMessages.json';
import { sendNotifcation } from '@/utils/notification';
import { IFileEditComponentProps } from 'interfaces/File/file';
import CloseImg from 'public/assets/close.svg';

import { PrimaryButton } from '../Button/PrimaryButton';

export default function FileEditComponent({
  setIsEditClicked,
  getFile,
  setErrorPage,
}: IFileEditComponentProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [cancelTokenSource, setCancelTokenSource] =
    useState<CancelTokenSource | null>(null);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const hiddenFileInput = useRef(null);

  const fileId = router.query.fileId as string;

  const handleDrop = async (acceptedFiles: File[]) => {
    let files = acceptedFiles[0];

    const options = {
      maxSizeMB: 1000,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };
    if (files?.type.includes('image')) {
      files = (await imageCompression(files, options)) as any;
    }

    setIsUploading(true);
    const source = axios.CancelToken.source();
    setCancelTokenSource(source);

    if (files.size > 20971520) {
      cancelTokenSource?.cancel();
      setIsUploading(false);
      sendNotifcation('Maximum allowed file size is 20MB', '', 'danger');
      return;
    }

    try {
      const onUploadProgress = (progressEvent: any) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setProgress(percentCompleted);
      };
      const recaptchaToken = await executeRecaptcha('file');
      const obj = {
        files,
        recaptchaToken,
        fileId,
        config: {
          onUploadProgress,
          cancelToken: source.token,
        },
      };
      setFileName(files?.name);
      const response = await editFile(obj);
      if (response.status === 200) {
        getFile(fileId);
        setIsEditClicked(false);
        setIsUploading(false);
      }
    } catch (error: any) {
      setIsUploading(false);
      if (error.response?.status >= 500) {
        setErrorPage(true);
        return;
      }
      switch (error.response?.status) {
        case 429: {
          sendNotifcation(error.response.data.message, '', 'danger');
          break;
        }
        case 403: {
          sendNotifcation(errorMessages[403], '', 'danger');
          break;
        }
        case 401: {
          sendNotifcation(errorMessages[401], '', 'danger');
          break;
        }
        default: {
          sendNotifcation('Unable to edit file', '', 'danger');
          break;
        }
      }
    }
  };

  const selectEditFile = (acceptedFiles: FileList) => {
    const editFileMock = Array.from(acceptedFiles);
    handleDrop(editFileMock);
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    maxFiles: 1,
    noClick: true,
  });

  return (
    <div
      {...getRootProps()}
      className="h-full relative w-full grid grid-rows-6 rounded-[8px] lg:col-span-2"
    >
      <div
        {...getInputProps()}
        className={`row-span-5 relative rounded-md ${
          isDragActive ? 'bg-yellow opacity-50 border-2 border-black' : 'border'
        } border-dashed flex items-center justify-center`}
      >
        <h4 className=" text-2xl">Drag and drop file</h4>
      </div>
      <input
        multiple={true}
        type={'file'}
        ref={hiddenFileInput}
        accept="*"
        style={{ display: 'none' }}
        onChange={(e) => selectEditFile(e.target.files)}
      />
      <div className="items-center grid grid-cols-2">
        <div>
          <PrimaryButton onClick={() => setIsEditClicked(false)}>
            <span>Back </span>
          </PrimaryButton>
        </div>
        <div className="flex justify-end">
          <PrimaryButton onClick={() => hiddenFileInput.current.click()}>
            <span>Select File </span>
          </PrimaryButton>
        </div>
      </div>
      {isUploading && (
        <>
          <section className="absolute top-0 justify-center flex backdrop-blur-md h-full items-center  w-full ">
            <div className="bg-white h-full w-full  flex items-center flex-col border-2  rounded-md border-dashed justify-center">
              <div className="text-center text-lg font-semibold">
                <h2>Uploading</h2>
              </div>
              <div className="flex mt-2 justify-center">{fileName}</div>
              <div className="border-2 relative border-gray-400 mt-2 mb-2 border-dashed flex pl-4 pr-1 py-1 rounded-lg h-16 w-[15rem] mx-auto">
                <div className="w-full px-2 my-auto ">
                  <div className="relative w-full h-2 bg-gray-200 rounded-full">
                    <div
                      style={{ width: `${progress}%` }}
                      className="bg-prp absolute inset-0 rounded-full z-10"
                    />
                  </div>
                </div>

                <button
                  onClick={() => {
                    cancelTokenSource?.cancel();
                    setIsUploading(false);
                  }}
                  className="grid w-20 h-full ml-4 bg-yellow rounded-xxs place-items-center"
                >
                  <img src={CloseImg.src} alt="closeImg" />
                </button>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
